// The CONTENT-TRANSLATION contract — the vocabulary and the wire shapes shared by the server that
// answers `POST /api/translations/resolve` and the `<Tx>` component that renders the answer.
// Shared for the same reason `language.ts` is shared: the moment the two realms carry their own
// copy of "what a translated field looks like", one of them starts lying about provenance.
//
// Nothing here executes anything. The read path's logic lives in
// `src/server/services/translationRead.ts`; the client's in `src/client/i18n/content.tsx`.

import type { Language, TranslationLang } from './language.js';

/** The entity kinds that carry translatable prose — mirrors the CHECK constraint on
 *  `translations.entity_type` and the branches of the `translation_sources` view. */
export const CONTENT_ENTITY_TYPES = [
  'requirement_revision', 'deliverable', 'deliverable_item', 'acceptance_criterion',
  'test_case', 'open_question', 'milestone', 'change_request', 'decision', 'risk',
  'timeline_event', 'portal_comment', 'document', 'proposal',
] as const;
export type ContentEntityType = (typeof CONTENT_ENTITY_TYPES)[number];
export const isContentEntityType = (v: unknown): v is ContentEntityType =>
  typeof v === 'string' && (CONTENT_ENTITY_TYPES as readonly string[]).includes(v);

/** `operator_prefs.content_language` — which language an operator READS engagement content in.
 *  'original' = the record as authored, always (the default: it claims nothing).
 *  'ui'       = follow the console language, so one switch moves both. */
export const CONTENT_LANG_PREFS = ['original', 'ui', 'de', 'en', 'fr'] as const;
export type ContentLangPref = (typeof CONTENT_LANG_PREFS)[number];
export const isContentLangPref = (v: unknown): v is ContentLangPref =>
  typeof v === 'string' && (CONTENT_LANG_PREFS as readonly string[]).includes(v);

/** A glossary term whose target rendering did not survive into a translation (§3.5). */
/**
 * Something worth a reader's attention about a machine row. Two kinds:
 *  · `term` (the original shape, and the default when `kind` is absent on rows written before
 *    this union existed) — an active glossary term whose target rendering did not come through.
 *  · `language` — the text is not even in the language it claims to be. Observed on prod: 10 of
 *    166 French rows came back in English, terminology correct, sentence not French. A reader
 *    must not be told "this is the French" about an English sentence.
 */
export type TranslationFlag =
  | { kind?: 'term'; term_id: number; expected: string; found: false }
  | { kind: 'language'; detected: string; expected: string };

/** True when the flag says the row is in the wrong language altogether. */
export const isLanguageFlag = (f: TranslationFlag): f is { kind: 'language'; detected: string; expected: string } =>
  (f as { kind?: string }).kind === 'language';

/** What a resolved field IS — see `resolveEntries` for the order these are decided in. */
export type ResolveStatus = 'original' | 'translated' | 'pending' | 'unavailable';

export interface ResolveEntry {
  status: ResolveStatus;
  /** The translation, or NULL meaning "render the record you already hold". */
  text: string | null;
  provenance: 'machine' | 'operator' | null;
  model: string | null;
  stale: boolean;
  flags: TranslationFlag[] | null;
  /** Resolved from `translations` with no live source behind it — frozen history (§1.3). */
  frozen?: true;
}

export interface OriginalInfo {
  lang: Language;
  detector: string;
}

export interface ResolveAnswer {
  lang: TranslationLang;
  originals: Record<string, OriginalInfo>;
  texts: Record<string, ResolveEntry>;
  pending: string[];
}

export const entityKey = (entityType: string, entityId: number): string => `${entityType}:${entityId}`;
export const fieldKey = (entityType: string, entityId: number, field: string): string =>
  `${entityType}:${entityId}:${field}`;

/** Term rows as the Terminology panel and the glossary routes exchange them. */
export interface GlossaryTerm {
  id: number;
  engagement_id: number;
  term_de: string;
  term_en: string;
  term_fr: string;
  invariant: boolean;
  note: string | null;
  source: 'operator' | 'seed_agent';
  status: 'proposed' | 'active' | 'retired';
  created_at: string;
  updated_at: string;
}
